import axios from 'axios';

type UploadJobStatus = 'queued' | 'processing' | 'completed' | 'failed';

interface UploadJob {
  id: string;
  status: UploadJobStatus;
  progress: number;
  message: string;
  fileId?: number;
  error?: string;
  warnings?: string[];
  diagnostics?: {
    sourceRows?: number;
    sourceColumns?: number;
    parsedRows?: number;
    acceptedRows?: number;
    irregularRows?: number;
    invalidTimestampRows?: number;
    invalidPositionRows?: number;
    delimiter?: string;
    encoding?: string;
    format?: string;
  };
}

interface UploadJobResponse {
  success: boolean;
  job: UploadJob;
}

interface PollUploadJobOptions {
  baseUrl: string;
  token: string;
  jobId: string;
  onUpdate: (job: UploadJob) => void;
}

const UPLOAD_JOB_POLL_INTERVAL_MS = 2500;
const UPLOAD_JOB_MAX_POLLS = 240;

export const pollUploadJob = async ({
  baseUrl,
  token,
  jobId,
  onUpdate,
}: PollUploadJobOptions): Promise<UploadJob> => {
  for (let attempt = 0; attempt < UPLOAD_JOB_MAX_POLLS; attempt += 1) {
    const response = await axios.get<UploadJobResponse>(
      `${baseUrl}/api/user/uploadJobs/${jobId}`,
      {
        headers: { Authorization: `Bearer ${token}` },
      },
    );
    const { job } = response.data;
    onUpdate(job);

    if (job.status === 'completed') {
      return job;
    }
    if (job.status === 'failed') {
      throw new Error(job.error || job.message || 'Upload job failed');
    }

    await new Promise((resolve) => {
      window.setTimeout(resolve, UPLOAD_JOB_POLL_INTERVAL_MS);
    });
  }

  throw new Error('Upload job timed out');
};
